/**
 * Step wizard. One decision at a time — the audience is often
 * reading in a second language on an old phone at eleven at night (§5
 * accessibility), and a single long form asks them to hold everything at
 * once.
 *
 * Accessibility rules this file keeps:
 *  - focus moves to the new step's heading, so a screen reader announces it
 *  - progress is a live region ("Step 3 of 5")
 *  - validation errors are announced, tied to their input by aria-describedby,
 *    and coloured amber — never red (prohibition 14)
 *  - every step is reachable by keyboard alone; nothing depends on hover
 *
 * The wizard NEVER gates the result (§9.2): it collects answers, and the
 * result appears in full the moment the last step is submitted.
 */

export interface WizardHooks {
  /** Called whenever the visible step changes, so conditional blocks refresh. */
  onStepChange: (step: number) => void;
  /** Fields that must be answered before leaving the given step. */
  requiredFor: (step: number) => Array<{ name: string; message: string }>;
  /**
   * Whether a step applies at all on the answers so far. A step that does not
   * apply is SKIPPED, not shown empty: telling someone "there is nothing to
   * answer here" wastes a click and makes them wonder what they missed.
   * Numbering and the rail count only the steps that apply.
   */
  isActive?: (step: number) => boolean;
}

function allSteps(doc: Document): number[] {
  return [...doc.querySelectorAll<HTMLElement>(".step")].map((s) => Number(s.dataset["step"]));
}

export class Wizard {
  private current = 1;
  private readonly steps: number[];

  constructor(
    private readonly form: HTMLFormElement,
    private readonly hooks: WizardHooks,
  ) {
    this.steps = allSteps(form.ownerDocument);
    this.wireNav();
    this.show(this.active()[0] ?? 1, { moveFocus: false });
  }

  /** Steps that apply on the answers given so far, in order. */
  private active(): number[] {
    return this.steps.filter((step) => this.hooks.isActive?.(step) !== false);
  }

  /** The next applicable step in the direction of travel, or null at the end. */
  private neighbour(from: number, direction: 1 | -1): number | null {
    const active = this.active();
    const ahead = direction === 1 ? active.filter((s) => s > from) : active.filter((s) => s < from).reverse();
    return ahead[0] ?? null;
  }

  get step(): number {
    return this.current;
  }

  /**
   * Re-evaluate which steps apply and redraw the count and rail. Needed
   * because an answer on the CURRENT step can change how many steps there
   * are — choosing "settlement" adds the Life in the UK step — and leaving
   * "Step 2 of 7" on screen when it has become 8 is a small lie.
   */
  /** Which step is showing. Used to avoid re-entering the results step. */
  currentStep(): number {
    return this.current;
  }

  refresh(): void {
    this.show(this.current, { moveFocus: false });
  }

  private el<T extends HTMLElement>(selector: string): T {
    const node = this.form.ownerDocument.querySelector<T>(selector);
    if (!node) throw new Error(`missing element: ${selector}`);
    return node;
  }

  private wireNav(): void {
    this.el<HTMLButtonElement>("#next-button").addEventListener("click", () => {
      if (!this.validate()) return;
      const next = this.neighbour(this.current, 1);
      if (next !== null) this.show(next);
    });
    this.el<HTMLButtonElement>("#back-button").addEventListener("click", () => {
      this.clearErrors();
      const previous = this.neighbour(this.current, -1);
      if (previous !== null) this.show(previous);
    });

    // Enter should advance rather than submit a half-finished form.
    this.form.addEventListener("keydown", (event) => {
      const key = (event as KeyboardEvent).key;
      const target = event.target as HTMLElement;
      if (key !== "Enter" || target.tagName === "BUTTON" || target.tagName === "TEXTAREA") return;
      const next = this.neighbour(this.current, 1);
      if (next !== null) {
        event.preventDefault();
        if (this.validate()) this.show(next);
      }
    });
  }

  show(step: number, options: { moveFocus?: boolean } = {}): void {
    const doc = this.form.ownerDocument;
    const active = this.active();
    // If the answers just made the current step inapplicable, land on the
    // nearest one that does apply rather than on a blank panel.
    this.current = active.includes(step) ? step : (this.neighbour(step, 1) ?? active[active.length - 1] ?? 1);

    for (const section of doc.querySelectorAll<HTMLElement>(".step")) {
      section.hidden = Number(section.dataset["step"]) !== this.current;
    }
    for (const rail of doc.querySelectorAll<HTMLElement>("[data-rail]")) {
      const index = Number(rail.dataset["rail"]);
      // A step that does not apply disappears from the rail too, so the rail
      // and the numbering always agree.
      rail.hidden = !active.includes(index);
      rail.classList.toggle("done", index < this.current);
      rail.classList.toggle("active", index === this.current);
    }

    const position = active.indexOf(this.current) + 1;
    this.el<HTMLElement>("#progress").textContent = `Step ${position} of ${active.length}`;
    this.el<HTMLButtonElement>("#back-button").hidden = this.neighbour(this.current, -1) === null;
    const isLast = this.neighbour(this.current, 1) === null;
    this.el<HTMLButtonElement>("#next-button").hidden = isLast;
    this.el<HTMLButtonElement>("#submit-button").hidden = !isLast;

    this.hooks.onStepChange(this.current);

    if (options.moveFocus !== false) {
      this.el<HTMLElement>(`#h-step-${this.current}`).focus();
    }
  }

  /** Validates the visible step. Returns true when it is safe to advance. */
  validate(): boolean {
    this.clearErrors();
    const missing = this.hooks
      .requiredFor(this.current)
      .filter(({ name }) => !this.hasAnswer(name));

    if (missing.length === 0) return true;

    for (const { name, message } of missing) this.markError(name, message);

    const first = missing[0]!;
    const summary = this.el<HTMLElement>("#step-error");
    summary.textContent =
      missing.length === 1 ? first.message : "Please answer the questions marked below.";
    summary.hidden = false;
    this.focusField(first.name);
    return false;
  }

  private hasAnswer(name: string): boolean {
    const nodes = this.form.querySelectorAll<HTMLInputElement>(`[name="${name}"]`);
    if (nodes.length === 0) return true; // not on this step (conditional block hidden)
    const first = nodes[0]!;
    // Checkboxes as well as radios: a checkbox group carries a value
    // attribute whether or not it is ticked, so testing `.value` would count
    // an untouched group of chips as answered.
    if (first.type === "radio" || first.type === "checkbox") {
      return [...nodes].some((node) => node.checked);
    }
    return first.value.trim() !== "";
  }

  private focusField(name: string): void {
    const node = this.form.querySelector<HTMLInputElement>(`[name="${name}"]`);
    node?.focus();
  }

  private markError(name: string, message: string): void {
    const inputs = this.form.querySelectorAll<HTMLInputElement>(`[name="${name}"]`);
    for (const input of inputs) input.setAttribute("aria-invalid", "true");

    const inline = this.form.ownerDocument.getElementById(`err-${name}`);
    if (inline) {
      inline.textContent = message;
      inline.hidden = false;
    }
    const group = this.form.querySelector<HTMLElement>(`[data-required="${name}"]`);
    group?.classList.add("needs-answer");
  }

  private clearErrors(): void {
    const summary = this.el<HTMLElement>("#step-error");
    summary.hidden = true;
    summary.textContent = "";
    for (const input of this.form.querySelectorAll("[aria-invalid]")) {
      input.removeAttribute("aria-invalid");
    }
    for (const inline of this.form.querySelectorAll<HTMLElement>(".field-error")) {
      inline.hidden = true;
      inline.textContent = "";
    }
    for (const group of this.form.querySelectorAll(".needs-answer")) {
      group.classList.remove("needs-answer");
    }
  }
}
